export interface JobTenureWindow {
  start: string
  end: string
  label: string
  week: string
}

export interface JobTenureChartData {
  labels: string[]
  cohort_size: number[]
  /** Backward-compatible alias used by the existing chart/callout. */
  people_count: number[]
  active_count: number[]
  active: Array<number | null>
  happy_count: number[]
  risk_count: number[]
  response_count: number[]
  happy: Array<number | null>
  worse: Array<number | null>
  feedback_count: number[]
  feedback_prompt_count: number[]
  feedback: Array<number | null>
  updated_at: string
  time_window?: JobTenureWindow
  quality_issues: string[]
  source: {
    population: string
    activity: string
    sentiment: string
    feedback: string
  }
}

const DEFAULT_LABELS = [
  'Probation (0-119d)',
  '120d to 1yr',
  '1 to 2yrs',
  '2 to 5yrs',
  '5+ yrs',
]

function count(value: any): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0
}

function valueAt(value: any, index: number): any {
  return value && value[index] !== undefined ? value[index] : undefined
}

function percentage(
  numerator: number,
  denominator: number,
  metric: string,
  index: number,
  issues: string[],
): number | null {
  // No denominator means "no observations", not a measured zero percent.
  if (denominator === 0) {
    if (numerator > 0) {
      issues.push(`${metric}_${index}: numerator ${numerator} has no denominator`)
    }
    return null
  }
  if (numerator > denominator) {
    issues.push(`${metric}_${index}: numerator ${numerator} exceeds denominator ${denominator}`)
  }
  return Math.round((Math.min(numerator, denominator) * 100) / denominator)
}

/**
 * Converts the legacy Firestore aggregate into an explicit chart contract.
 *
 * Happiness/Risk are shares of happiness check-ins (repeat check-ins allowed).
 * Active is distinct active people divided by current cohort headcount.
 * Open Feedback is completed open-ended responses divided by open-ended prompts.
 */
export function buildJobTenureChartData(
  raw: any,
  timeWindow?: JobTenureWindow,
): JobTenureChartData {
  const issues: string[] = []
  const labels = DEFAULT_LABELS.map((fallback, index) => {
    return String(valueAt(raw && raw.label, index) || fallback)
  })
  const cohortSize: number[] = []
  const activeCount: number[] = []
  const happyCount: number[] = []
  const riskCount: number[] = []
  const responseCount: number[] = []
  const feedbackCount: number[] = []
  const feedbackPromptCount: number[] = []
  const active: Array<number | null> = []
  const happy: Array<number | null> = []
  const worse: Array<number | null> = []
  const feedback: Array<number | null> = []

  for (let index = 0; index < 5; index++) {
    const cohort = count(valueAt(raw && raw.total, index))
    const activePeople = count(raw && raw[`active_${index}`])
    const happyResponses = count(raw && raw[`happy_${index}`])
    const riskResponses = count(raw && raw[`worse_${index}`])
    const sentimentResponses = count(raw && raw[`happy_total_${index}`])
    const openFeedbackResponses = count(raw && raw[`feedback_${index}`])
    const openFeedbackPrompts = count(raw && raw[`feedback_total_${index}`])

    if (happyResponses + riskResponses > sentimentResponses) {
      issues.push(
        `sentiment_${index}: happiness plus risk exceeds total responses`,
      )
    }

    cohortSize.push(cohort)
    activeCount.push(activePeople)
    happyCount.push(happyResponses)
    riskCount.push(riskResponses)
    responseCount.push(sentimentResponses)
    feedbackCount.push(openFeedbackResponses)
    feedbackPromptCount.push(openFeedbackPrompts)
    active.push(percentage(activePeople, cohort, 'active', index, issues))
    happy.push(percentage(happyResponses, sentimentResponses, 'happy', index, issues))
    worse.push(percentage(riskResponses, sentimentResponses, 'risk', index, issues))
    feedback.push(percentage(openFeedbackResponses, openFeedbackPrompts, 'feedback', index, issues))
  }

  return {
    labels,
    cohort_size: cohortSize,
    people_count: cohortSize,
    active_count: activeCount,
    active,
    happy_count: happyCount,
    risk_count: riskCount,
    response_count: responseCount,
    happy,
    worse,
    feedback_count: feedbackCount,
    feedback_prompt_count: feedbackPromptCount,
    feedback,
    updated_at: raw && raw.date ? String(raw.date) : '',
    time_window: timeWindow,
    quality_issues: issues,
    source: {
      population: 'Realtime Database users grouped by workdate',
      activity: 'Firestore weeky_activate_users_check (distinct people this ISO week)',
      sentiment: 'Firestore users_questions happiness check-ins this ISO week',
      feedback: 'Firestore users_questions open-ended prompts this ISO week',
    },
  }
}
